import { useState, useEffect } from 'react'
import {
  Table,
  Col,
  Row,
  Button,
  Select,
  Tooltip,
  Typography,
  message,
} from 'antd'
import { SearchOutlined, EditOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { getEventDayPrices, getEvents, getEventsByCountry } from 'src/api/event'
import { fetchCountryProvider } from 'src/api/authen'
import { manualFetch } from 'src/utils/fetch'
import './Prices.css'

const { Title, Text } = Typography

const AsyncImage = ({ src, alt, style }) => {
  const [imageUrl, setImageUrl] = useState(null)

  useEffect(() => {
    if (!src) return
    let revokedUrl = null

    const loadImage = async () => {
      const response = await manualFetch(src, {
        headers: { Accept: 'image/*' },
      })
      const blob = await response.blob()
      revokedUrl = URL.createObjectURL(blob)
      setImageUrl(revokedUrl)
    }

    loadImage()
    return () => revokedUrl && URL.revokeObjectURL(revokedUrl)
  }, [src])

  return imageUrl ? (
    <img src={imageUrl} alt={alt} style={style} />
  ) : (
    <div style={{ ...style, background: '#1f1f1f' }} />
  )
}

export const Prices = () => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [countries, setCountries] = useState([])
  const [events, setEvents] = useState([])
  const [country, setCountry] = useState(null)
  const [eventId, setEventId] = useState()
  const [day, setDay] = useState(null)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [dayOptions, setDayOptions] = useState([])

  const navigate = useNavigate()

  const fetchCountry = async () => {
    const res = await fetchCountryProvider()
    setCountries([
      { label: 'All', value: null },
      ...res.data.map((c) => ({
        label: c.countryName,
        value: c.countryCode,
      })),
    ])
  }

  const fetchEvents = async (selectedCountry = null) => {
    try {
      const res = selectedCountry
        ? await getEventsByCountry(selectedCountry)
        : await getEvents()

      setEvents(
        res.data.map((e) => ({
          label: e.name,
          value: e.id,
          days: e.days,
        }))
      )
    } catch (e) {
      message.error('Failed to load events')
    }
  }

  const fetchData = async () => {
    if (!eventId) return

    setLoading(true)

    try {
      const res = await getEventDayPrices(eventId, day)

      const mapped = res.map((item) => ({
        key: item.place,
        place: item.place,
        ...item.price,
        imageUrl: item.price.images?.[0],
      }))

      setData(mapped)
    } catch (e) {
      message.error('Invalid Day')
    } finally {
      setLoading(false)
    }
  }

  const navigateToEdit = (record) => {
    navigate(`/events/prices/edit/${record.id}`)
  }

  const columns = [
    {
      title: 'Place',
      dataIndex: 'place',
    },
    {
      title: 'Image',
      render: (_, record) => (
        <AsyncImage
          src={record.imageUrl}
          alt="img"
          style={{ width: 60, height: 60, borderRadius: 4 }}
        />
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
    },
    {
      title: 'Points',
      dataIndex: 'points',
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      render: (text = '') => {
        const short = text.length > 115 ? `${text.slice(0, 115)}...` : text
        return (
          <Tooltip title={text}>
            <span>{short}</span>
          </Tooltip>
        )
      },
    },
    {
      title: 'Action',
      render: (_, record) => (
        <Button
          size="small"
          icon={<EditOutlined />}
          className="btn-primary"
          onClick={() => {
            navigateToEdit(record)
          }}
        >
          Edit
        </Button>
      ),
    },
  ]

  useEffect(() => {
    if (eventId) {
      fetchData()
    }
  }, [eventId, day])

  useEffect(() => {
    fetchEvents(country)
    setEventId(null)
    setSelectedEvent(null)
    setDay(null)
    setDayOptions([])
    setData([])
  }, [country])

  useEffect(() => {
    if (events.length > 0) {
      setEventId(events[0].value)
    }
  }, [events])

  useEffect(() => {
    if (!eventId || !events.length) return

    const event = events.find((e) => e.value === eventId)
    if (!event?.days) return

    setSelectedEvent(event)

    const options = [
      { label: 'Finale', value: null },
      ...Array.from({ length: event.days }, (_, i) => ({
        label: `Day ${i + 1}`,
        value: i + 1,
      })),
    ]

    setDayOptions(options)
    setDay(null)
  }, [eventId, events])

  useEffect(() => {
    fetchCountry()
    fetchEvents()
  }, [])

  return (
    <div style={{ padding: 24, background: '#000', minHeight: '100vh' }}>
      <Title level={4} style={{ color: '#fff' }}>
        Event Day Prices
      </Title>

      <Row gutter={10}>
        <Col span={4}>
          <Select
            placeholder="Country"
            options={countries}
            value={country}
            onChange={(value) => {
              setCountry(value)
              setEventId(null)
            }}
            style={{ width: '100%' }}
          />
        </Col>
        <Col span={4}>
          <Select
            placeholder="Event"
            options={events}
            value={eventId}
            onChange={(value) => {
              const event = events.find((e) => e.value === value)
              setEventId(value)
              setSelectedEvent(event)
              if (event?.days) {
                const options = [
                  { label: 'Finale', value: null },
                  ...Array.from({ length: event.days }, (_, i) => ({
                    label: `Day ${i + 1}`,
                    value: i + 1,
                  })),
                ]
                setDayOptions(options)
                setDay(null)
              }
            }}
            style={{ width: '100%' }}
          />
        </Col>

        <Col span={3}>
          <Select
            placeholder="Day"
            value={day}
            options={dayOptions}
            onChange={setDay}
            style={{ width: '100%' }}
          />
        </Col>

        <Col span={3}>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            loading={loading}
            onClick={fetchData}
            disabled={!eventId}
            className="btn-primary"
          >
            Search
          </Button>
        </Col>
      </Row>

      <Table
        style={{ marginTop: 20 }}
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={false}
      />
    </div>
  )
}

export default Prices
